import { createContext } from 'preact';
import { useContext, useState, useCallback, useEffect } from 'preact/hooks';
import { api, onLogout } from '../api/client';
import type { StatsResponse } from '../api/types';
import type { ComponentChildren } from 'preact';

interface AuthContextValue {
  secret: string | null;
  login: (secret: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ComponentChildren }) {
  const [secret, setSecret] = useState<string | null>(
    () => sessionStorage.getItem('adminSecret'),
  );

  const logout = useCallback(() => {
    sessionStorage.removeItem('adminSecret');
    setSecret(null);
  }, []);

  useEffect(() => {
    onLogout(logout);
  }, [logout]);

  const login = useCallback(async (value: string) => {
    sessionStorage.setItem('adminSecret', value);
    try {
      await api.get<StatsResponse>('/api/admin/stats');
      setSecret(value);
    } catch {
      sessionStorage.removeItem('adminSecret');
      throw new Error('Invalid admin secret');
    }
  }, []);

  return (
    <AuthContext.Provider value={{ secret, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
